'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, RefreshCw, Plus, Trash2, Tag, Zap, MessageSquare, ChevronDown, ChevronUp, Info, ArrowRight, GitBranch, Workflow, Link2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import DragDropFlowBuilder from './DragDropFlowBuilder';

interface OptionConfig {
  option_number: number;
  option_label: string;
  action_type: 'enroll_campaign' | 'send_template' | 'none';
  campaign_name: string;
  template_name: string;
  keywords: string[];
  linked_campaign_id: string | null;
  linked_template_key: string | null;
}

interface Campaign {
  id: string;
  name: string;
}

interface TemplateSid {
  template_key: string;
  label: string | null;
}

const ACTION_TYPES = [
  { value: 'enroll_campaign', label: 'Enroll into Campaign', icon: Zap, color: 'text-blue-600 bg-blue-50' },
  { value: 'send_template', label: 'Send Template Message', icon: MessageSquare, color: 'text-emerald-600 bg-emerald-50' },
  { value: 'none', label: 'No Action', icon: Info, color: 'text-gray-500 bg-gray-50' },
] as const;

const OPTION_COLORS = [
  { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-600', text: 'text-violet-700', light: 'bg-violet-100', dot: 'bg-violet-500' },
  { bg: 'bg-sky-50', border: 'border-sky-200', badge: 'bg-sky-600', text: 'text-sky-700', light: 'bg-sky-100', dot: 'bg-sky-500' },
  { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-100', dot: 'bg-amber-500' },
  { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-100', dot: 'bg-rose-500' },
  { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-600', text: 'text-teal-700', light: 'bg-teal-100', dot: 'bg-teal-500' },
];

function getColor(index: number) {
  return OPTION_COLORS[index % OPTION_COLORS.length];
}

// ── Workflow Diagram ──────────────────────────────────────────────────────────
function WorkflowDiagram({ configs }: { configs: OptionConfig[] }) {
  const activeConfigs = configs.filter((c) => c.action_type !== 'none');

  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-2.5 px-5 py-3.5 bg-gradient-to-r from-[#0a2d4d] to-[#0d3a63] text-white">
        <GitBranch size={16} className="opacity-80" />
        <span className="text-sm font-semibold tracking-tight">Workflow 2 — Response Routing</span>
        <span className="ml-auto text-[11px] opacity-60 font-normal">Live preview of current mappings</span>
      </div>

      <div className="px-5 py-5">
        <div className="flex justify-center mb-5">
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex items-center gap-2 bg-[#0a2d4d] text-white text-xs font-semibold px-4 py-2.5 rounded-xl shadow-sm">
              <MessageSquare size={13} />
              Lead replies to List Picker
            </div>
            <div className="w-px h-5 bg-gray-300" />
            <div className="w-2 h-2 rounded-full bg-gray-400" />
          </div>
        </div>

        {configs.length === 0 ? (
          <p className="text-center text-xs text-gray-400 py-4">No options configured yet.</p>
        ) : (
          <div className={`grid gap-3 ${configs.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' : configs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {configs.map((config, index) => {
              const color = getColor(index);
              const isEnroll = config.action_type === 'enroll_campaign';
              const isTemplate = config.action_type === 'send_template';
              const isNone = config.action_type === 'none';

              return (
                <div key={`flow-${config.option_number}`} className="flex flex-col items-center gap-0">
                  <div className={`w-full rounded-xl border ${color.border} ${color.bg} px-3 py-2.5 text-center`}>
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <span className={`w-5 h-5 rounded-full ${color.badge} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}>
                        {config.option_number}
                      </span>
                      <span className={`text-[11px] font-semibold ${color.text} leading-tight`}>
                        {config.option_label || `Option ${config.option_number}`}
                      </span>
                    </div>
                    {config.keywords.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-1 mt-1">
                        {config.keywords.slice(0, 3).map((kw) => (
                          <span key={kw} className="text-[9px] bg-white/70 border border-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full font-mono">
                            &quot;{kw}&quot;
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Linked campaign/template badge */}
                    {config.linked_campaign_id && (
                      <div className="mt-1.5 flex items-center justify-center gap-1">
                        <Link2 size={9} className="text-indigo-400" />
                        <span className="text-[9px] text-indigo-500 font-medium">Campaign: {config.linked_campaign_id}</span>
                      </div>
                    )}
                    {config.linked_template_key && (
                      <div className="mt-1.5 flex items-center justify-center gap-1">
                        <FileText size={9} className="text-purple-400" />
                        <span className="text-[9px] text-purple-500 font-medium">Template: {config.linked_template_key}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="w-px h-4 bg-gray-300" />
                    <ArrowRight size={12} className="text-gray-400 rotate-90" />
                  </div>

                  {isEnroll && (
                    <div className="w-full bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-0.5">
                        <Zap size={11} className="text-blue-600" />
                        <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Enroll Campaign</span>
                      </div>
                      <span className="text-xs font-bold text-blue-800 font-mono">{config.campaign_name || '—'}</span>
                      <div className="mt-1">
                        <span className="text-[9px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">sendImmediately: true</span>
                      </div>
                    </div>
                  )}

                  {isTemplate && (
                    <div className="w-full bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-0.5">
                        <MessageSquare size={11} className="text-emerald-600" />
                        <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">Send Template</span>
                      </div>
                      <span className="text-xs font-bold text-emerald-800 font-mono">{config.template_name || '—'}</span>
                    </div>
                  )}

                  {isNone && (
                    <div className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Info size={11} className="text-gray-400" />
                        <span className="text-[10px] font-medium text-gray-400">No Action</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeConfigs.length > 0 && (
          <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-2 justify-center">
            {activeConfigs.map((c) => {
              const color = getColor(configs.indexOf(c));
              return (
                <div key={`summary-${c.option_number}`} className="flex items-center gap-1.5 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                  <span className={`w-4 h-4 rounded-full ${color.badge} text-white text-[9px] font-bold flex items-center justify-center`}>
                    {c.option_number}
                  </span>
                  <span className="font-medium truncate max-w-[100px]">{c.option_label}</span>
                  <ArrowRight size={10} className="text-gray-400 shrink-0" />
                  <span className={`font-semibold ${c.action_type === 'enroll_campaign' ? 'text-blue-600' : 'text-emerald-600'}`}>
                    {c.action_type === 'enroll_campaign' ? c.campaign_name : c.template_name}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function ListPickerConfigClient() {
  const [configs, setConfigs] = useState<OptionConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [newKeyword, setNewKeyword] = useState<Record<number, string>>({});
  const [showFlowBuilder, setShowFlowBuilder] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templateSids, setTemplateSids] = useState<TemplateSid[]>([]);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/list-picker-config');
      const data = await res.json() as { configs?: OptionConfig[]; error?: string };
      if (data.configs) {
        setConfigs(data.configs.map((c) => ({
          ...c,
          campaign_name: c.campaign_name ?? '',
          template_name: c.template_name ?? '',
          keywords: c.keywords ?? [],
          linked_campaign_id: c.linked_campaign_id ?? null,
          linked_template_key: c.linked_template_key ?? null,
        })));
      } else {
        toast.error(data.error ?? 'Failed to load config');
      }
    } catch {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaignsAndTemplates = useCallback(async () => {
    try {
      const [campRes, tplRes] = await Promise.all([
        fetch('/api/list-picker-config?resource=campaigns'),
        fetch('/api/list-picker-config?resource=templates'),
      ]);
      const campData = await campRes.json() as { campaigns?: Campaign[] };
      const tplData = await tplRes.json() as { templates?: TemplateSid[] };
      if (campData.campaigns) setCampaigns(campData.campaigns);
      if (tplData.templates) setTemplateSids(tplData.templates);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    void fetchConfigs();
    void fetchCampaignsAndTemplates();
  }, [fetchConfigs, fetchCampaignsAndTemplates]);

  const updateConfig = (index: number, field: keyof OptionConfig, value: unknown) => {
    setConfigs((prev) => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const addKeyword = (index: number) => {
    const kw = (newKeyword[index] ?? '').trim().toLowerCase();
    if (!kw) return;
    const existing = configs[index]?.keywords ?? [];
    if (existing.includes(kw)) {
      toast.error('Keyword already exists');
      return;
    }
    updateConfig(index, 'keywords', [...existing, kw]);
    setNewKeyword((prev) => ({ ...prev, [index]: '' }));
  };

  const removeKeyword = (optIndex: number, kw: string) => {
    const existing = configs[optIndex]?.keywords ?? [];
    updateConfig(optIndex, 'keywords', existing.filter((k) => k !== kw));
  };

  const addOption = () => {
    const nextNum = configs.length + 1;
    setConfigs((prev) => [
      ...prev,
      {
        option_number: nextNum,
        option_label: `Option ${nextNum}`,
        action_type: 'none',
        campaign_name: '',
        template_name: '',
        keywords: [String(nextNum)],
        linked_campaign_id: null,
        linked_template_key: null,
      },
    ]);
    setExpandedIndex(configs.length);
  };

  const removeOption = (index: number) => {
    setConfigs((prev) => {
      const updated = prev.filter((_, i) => i !== index).map((c, i) => ({ ...c, option_number: i + 1 }));
      return updated;
    });
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleSave = async () => {
    for (const c of configs) {
      if (!c.option_label.trim()) {
        toast.error(`Option ${c.option_number}: Label is required`);
        return;
      }
      if (c.action_type === 'enroll_campaign' && !c.campaign_name.trim()) {
        toast.error(`Option ${c.option_number}: Campaign name is required`);
        return;
      }
      if (c.action_type === 'send_template' && !c.template_name.trim()) {
        toast.error(`Option ${c.option_number}: Template name is required`);
        return;
      }
    }

    setSaving(true);
    try {
      const res = await fetch('/api/list-picker-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ configs }),
      });
      const data = await res.json() as { success?: boolean; error?: string };
      if (data.success) {
        toast.success('Mapping saved successfully');
      } else {
        toast.error(data.error ?? 'Failed to save');
      }
    } catch {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyFlowBuilder = (options: Array<{
    option_number: number;
    option_label: string;
    action_type: 'enroll_campaign' | 'send_template' | 'none';
    campaign_name: string;
    template_name: string;
    keywords: string[];
  }>) => {
    setConfigs(options.map((o) => ({ ...o, linked_campaign_id: null, linked_template_key: null })));
    setShowFlowBuilder(false);
    setExpandedIndex(0);
    toast.success('Flow applied! Review and save your changes.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={22} className="animate-spin text-[#0a2d4d]/40" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {showFlowBuilder && (
        <DragDropFlowBuilder
          onClose={() => setShowFlowBuilder(false)}
          onApply={handleApplyFlowBuilder}
        />
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-[#0a2d4d] tracking-tight">List Picker Response Mapping</h1>
            <p className="text-sm text-gray-500 mt-1">
              Configure what happens when a lead selects an option from a WhatsApp list picker message.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowFlowBuilder(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#0a2d4d] border border-[#0a2d4d]/30 rounded-lg hover:bg-[#0a2d4d]/5 transition-colors"
            >
              <Workflow size={14} />
              Flow Builder
            </button>
            <button
              onClick={() => void fetchConfigs()}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={14} />
              Reload
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-[#0a2d4d] text-white rounded-lg hover:bg-[#0d3a63] disabled:opacity-60 transition-colors"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Info banner */}
        <div className="mt-4 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <Info size={15} className="text-blue-500 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700 leading-relaxed">
            These mappings are used by the Twilio webhook to detect which option a lead selected and trigger the corresponding action automatically. Keywords are matched case-insensitively against the incoming message body.
          </p>
        </div>

        {/* Linking approaches explanation */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="flex items-start gap-2.5 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
            <Link2 size={14} className="text-indigo-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-indigo-700 mb-0.5">Campaign-Based Matching</p>
              <p className="text-[11px] text-indigo-600 leading-relaxed">
                Link a config to a campaign. The webhook checks which campaign last messaged the lead and applies the matching flow — prevents cross-campaign reply collisions.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3">
            <FileText size={14} className="text-purple-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-purple-700 mb-0.5">Template-Based Matching</p>
              <p className="text-[11px] text-purple-600 leading-relaxed">
                Link a config to a template. The webhook checks which template originally triggered the reply and applies the matching flow — ideal when the same campaign uses multiple list pickers.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Workflow Diagram ── */}
      <WorkflowDiagram configs={configs} />

      {/* Option Cards */}
      <div className="space-y-3">
        {configs.map((config, index) => {
          const color = getColor(index);
          const isExpanded = expandedIndex === index;
          const actionDef = ACTION_TYPES.find((a) => a.value === config.action_type);
          const ActionIcon = actionDef?.icon ?? Info;

          return (
            <div
              key={`option-${config.option_number}`}
              className={`border rounded-xl overflow-hidden transition-all duration-200 ${color.border} ${color.bg}`}
            >
              {/* Card Header */}
              <div
                className="flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none"
                onClick={() => setExpandedIndex(isExpanded ? null : index)}
              >
                <span className={`w-7 h-7 rounded-full ${color.badge} text-white text-xs font-bold flex items-center justify-center shrink-0`}>
                  {config.option_number}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${color.text} truncate`}>{config.option_label || `Option ${config.option_number}`}</p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${actionDef?.color ?? 'text-gray-500 bg-gray-100'}`}>
                      <ActionIcon size={10} />
                      {actionDef?.label ?? 'No Action'}
                    </span>
                    {config.action_type === 'enroll_campaign' && config.campaign_name && (
                      <span className="text-[11px] text-gray-500 truncate">→ {config.campaign_name}</span>
                    )}
                    {config.action_type === 'send_template' && config.template_name && (
                      <span className="text-[11px] text-gray-500 truncate">→ {config.template_name}</span>
                    )}
                    {config.linked_campaign_id && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded-full">
                        <Link2 size={9} />
                        {config.linked_campaign_id}
                      </span>
                    )}
                    {config.linked_template_key && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-purple-600 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded-full">
                        <FileText size={9} />
                        {config.linked_template_key}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {configs.length > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeOption(index); }}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Remove option"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                  {isExpanded ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
                </div>
              </div>

              {/* Expanded Body */}
              {isExpanded && (
                <div className="bg-white border-t border-gray-100 px-4 py-4 space-y-4">
                  {/* Option Label */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Option Label</label>
                    <input
                      type="text"
                      value={config.option_label}
                      onChange={(e) => updateConfig(index, 'option_label', e.target.value)}
                      placeholder="e.g. MBBS / BDS Admission"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0a2d4d]/20 focus:border-[#0a2d4d]/40"
                    />
                  </div>

                  {/* Action Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Action on Selection</label>
                    <div className="grid grid-cols-3 gap-2">
                      {ACTION_TYPES.map((at) => {
                        const AtIcon = at.icon;
                        const selected = config.action_type === at.value;
                        return (
                          <button
                            key={at.value}
                            onClick={() => updateConfig(index, 'action_type', at.value)}
                            className={`flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all ${
                              selected
                                ? 'border-[#0a2d4d] bg-[#0a2d4d] text-white shadow-sm'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <AtIcon size={15} />
                            {at.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Campaign Name */}
                  {config.action_type === 'enroll_campaign' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Campaign Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={config.campaign_name}
                        onChange={(e) => updateConfig(index, 'campaign_name', e.target.value)}
                        placeholder="e.g. workflow3"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0a2d4d]/20 focus:border-[#0a2d4d]/40"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">Must match the campaign type used in the drip API (e.g. workflow3, workflow4)</p>
                    </div>
                  )}

                  {/* Template Name */}
                  {config.action_type === 'send_template' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Template Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={config.template_name}
                        onChange={(e) => updateConfig(index, 'template_name', e.target.value)}
                        placeholder="e.g. other"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0a2d4d]/20 focus:border-[#0a2d4d]/40"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">Must match the template name registered in Twilio / WhatsApp templates</p>
                    </div>
                  )}

                  {/* ── Linking Section ── */}
                  <div className="border border-dashed border-gray-200 rounded-xl p-4 space-y-4 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Link2 size={13} className="text-gray-400" />
                      <span className="text-xs font-semibold text-gray-600">Webhook Matching Context</span>
                      <span className="text-[10px] text-gray-400 font-normal ml-1">(optional — improves accuracy when multiple list pickers exist)</span>
                    </div>

                    {/* Link to Campaign */}
                    <div>
                      <label className="block text-xs font-semibold text-indigo-700 mb-1.5 flex items-center gap-1.5">
                        <Link2 size={11} className="text-indigo-500" />
                        Link to Campaign
                      </label>
                      <select
                        value={config.linked_campaign_id ?? ''}
                        onChange={(e) => updateConfig(index, 'linked_campaign_id', e.target.value || null)}
                        className="w-full px-3 py-2 text-sm border border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white text-gray-700"
                      >
                        <option value="">— Not linked to a campaign —</option>
                        {campaigns.map((c) => (
                          <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-indigo-500 mt-1">
                        The webhook will only apply this flow if the last outbound message to the lead came from this campaign.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-[10px] text-gray-400 font-medium">OR</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>

                    {/* Link to Template */}
                    <div>
                      <label className="block text-xs font-semibold text-purple-700 mb-1.5 flex items-center gap-1.5">
                        <FileText size={11} className="text-purple-500" />
                        Link to Template
                      </label>
                      <select
                        value={config.linked_template_key ?? ''}
                        onChange={(e) => updateConfig(index, 'linked_template_key', e.target.value || null)}
                        className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white text-gray-700"
                      >
                        <option value="">— Not linked to a template —</option>
                        {templateSids.map((t) => (
                          <option key={t.template_key} value={t.template_key}>{t.label ?? t.template_key} ({t.template_key})</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-purple-500 mt-1">
                        The webhook will only apply this flow if the lead&apos;s last received message used this template.
                      </p>
                    </div>
                  </div>

                  {/* Keywords */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Match Keywords
                      <span className="ml-1 font-normal text-gray-400">(case-insensitive, matched against incoming message)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                      {config.keywords.map((kw) => (
                        <span
                          key={kw}
                          className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full"
                        >
                          <Tag size={10} className="text-gray-400" />
                          {kw}
                          <button
                            onClick={() => removeKeyword(index, kw)}
                            className="ml-0.5 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {config.keywords.length === 0 && (
                        <span className="text-xs text-gray-400 italic">No keywords — option won&apos;t be matched</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newKeyword[index] ?? ''}
                        onChange={(e) => setNewKeyword((prev) => ({ ...prev, [index]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(index); } }}
                        placeholder="Add keyword and press Enter"
                        className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0a2d4d]/20 focus:border-[#0a2d4d]/40"
                      />
                      <button
                        onClick={() => addKeyword(index)}
                        className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Option */}
      <button
        onClick={addOption}
        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-[#0a2d4d]/30 hover:text-[#0a2d4d] hover:bg-[#0a2d4d]/5 transition-all"
      >
        <Plus size={15} />
        Add Another Option
      </button>

      {/* Save Footer */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={() => void handleSave()}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold bg-[#0a2d4d] text-white rounded-xl hover:bg-[#0d3a63] disabled:opacity-60 transition-colors shadow-sm"
        >
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
}
